import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getImagePath } from '../model/yogaModel';

const loadImage = (src) => new Promise((resolve) => {
  const image = new Image();

  image.onload = async () => {
    if (typeof image.decode === 'function') {
      try {
        await image.decode();
      } catch (error) {
        // The image can still be displayed when decoding is handled by the browser.
      }
    }

    resolve(true);
  };
  image.onerror = () => resolve(false);
  image.src = src;
});

function MainPageView({ animateEntry, imageSources, renderHeaderOptions, onStartSequence }) {
  const gridRef = useRef(null);
  const imageRefs = useRef([]);
  const sequenceColors = [
    '#D8D1C3',
    '#B7B5AE',
    '#C8B8AA',
    '#AEB8B2',
    '#D2C7A8',
  ];

  const [hoverLeft, setHoverLeft] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hoveredImage, setHoveredImage] = useState(null);
  const [activeMobileImage, setActiveMobileImage] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  // Enable page scrolling on mobile layouts and remove the classes on cleanup.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    const updatePageScroll = () => {
      const method = mediaQuery.matches ? 'add' : 'remove';

      document.documentElement.classList[method]('main-page-scroll');
      document.body.classList[method]('main-page-scroll');
    };

    updatePageScroll();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updatePageScroll);
    } else {
      mediaQuery.addListener(updatePageScroll);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', updatePageScroll);
      } else {
        mediaQuery.removeListener(updatePageScroll);
      }
      document.documentElement.classList.remove('main-page-scroll');
      document.body.classList.remove('main-page-scroll');
    };
  }, []);

  const previewCards = useMemo(() => (
    hoveredImage === null
      ? []
      : imageSources[hoveredImage].previewFrames.map((frame) => ({
        frame,
        src: getImagePath(
          `${imageSources[hoveredImage].id}/${imageSources[hoveredImage].previewDirectory || 'preview'}/frame-${String(frame).padStart(2, '0')}.jpg`,
        ),
        label: String(frame).padStart(2, '0'),
      }))
  ), [hoveredImage, imageSources]);

  const handleMouseMove = (event) => {
    if (!gridRef.current) return;

    const gridRect = gridRef.current.getBoundingClientRect();

    setTooltipPosition({
      x: event.clientX - gridRect.left,
      y: event.clientY - gridRect.top,
    });
  };

  const handleImageClick = (image, index) => {
    if (!image.hasSequence) return;

    setHoveredImage(null);
    setShowPreview(false);
    onStartSequence(image);
  };

  const handleGridScroll = () => {
    const grid = gridRef.current;
    if (!grid || window.innerWidth > 768) return;

    const gridTop = grid.getBoundingClientRect().top;
    let closestIndex = 0;
    let closestDistance = Infinity;

    imageRefs.current.forEach((el, index) => {
      if (!el) return;

      const distance = Math.abs(el.getBoundingClientRect().top - gridTop);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveMobileImage(closestIndex);
  };

  // Preload and decode every pose preview before revealing the hover panel.
  useEffect(() => {
    if (hoveredImage === null) {
      setShowPreview(false);
      return undefined;
    }

    setShowPreview(false);

    const el = imageRefs.current[hoveredImage];

    if (el) {
      setHoverLeft(el.offsetLeft);
    }

    let cancelled = false;
    let timer;

    const minimumDelay = new Promise((resolve) => {
      timer = setTimeout(resolve, 300);
    });

    Promise.all([
      Promise.all(previewCards.map((preview) => loadImage(preview.src))),
      minimumDelay,
    ]).then(([loadedImages]) => {
      if (!cancelled && loadedImages.every(Boolean)) {
        setShowPreview(true);
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hoveredImage, previewCards]);

  return (
    <div className={`App main-page ${animateEntry ? 'main-page-fade-in' : ''}`}>
      <header className="top-row">
        <h1 className="main-title">
          ASHTANGA YOGA
        </h1>

        {renderHeaderOptions()}
      </header>

      <section
        ref={gridRef}
        className={`image-grid ${hoveredImage !== null ? 'active-hover' : ''}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredImage(null)}
        onScroll={handleGridScroll}
      >
        {hoveredImage !== null && (
          <div
            className={`hover-practice-name ${imageSources[hoveredImage].hasSequence ? '' : 'disabled'}`}
            style={{ left: `${hoverLeft}px` }}
          >
            {imageSources[hoveredImage].name}
          </div>
        )}

        {hoveredImage !== null && (
          <div
            className="start-tooltip"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
            }}
          >
            {imageSources[hoveredImage].hasSequence ? 'start sequence' : 'coming soon'}
          </div>
        )}

        {imageSources.map((image, index) => {
          const isHovered = hoveredImage === index;
          const isActiveMobile = activeMobileImage === index;
          const mobileTabIndex = index < activeMobileImage ? index : index - 1;
          const sequenceColor = sequenceColors[index % sequenceColors.length];
          return (
            <div
              key={image.src}
              ref={(el) => {
                imageRefs.current[index] = el;
              }}
              className={`image-card ${isHovered ? 'hovered' : 'thumbnail'} ${isActiveMobile ? 'mobile-active' : 'mobile-collapsed'} ${image.hasSequence ? '' : 'disabled'}`}
              style={{
                '--sequence-color': sequenceColor,
                ...(isActiveMobile ? {} : { '--mobile-tab-index': mobileTabIndex }),
              }}
              onMouseEnter={(e) => {
                if (hoveredImage !== index) {
                  setShowPreview(false);
                }
                setHoveredImage(index);
                setHoverLeft(e.currentTarget.offsetLeft);
              }}
              onClick={() => handleImageClick(image, index)}
            >
              <div className="practice-name">
                {image.name}
                {!image.hasSequence && (
                  <span className="mobile-coming-soon"> · coming soon</span>
                )}
              </div>
              <div className="image-frame">
                <img
                  src={image.src}
                  alt={image.name}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                />
              </div>
            </div>
          );
        })}

        {hoveredImage !== null && showPreview && previewCards.length > 0 && (
          <div
            className={`preview-panel ${imageSources[hoveredImage].hasSequence ? '' : 'disabled'}`}
            style={{ left: `${hoverLeft}px` }}
          >
            {previewCards.map((preview) => (
              <div
                key={preview.frame}
                className="preview-card"
              >
                <img
                  src={preview.src}
                  alt={`Preview ${preview.label}`}
                  decoding="async"
                />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default MainPageView;
